/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import {Moment} from 'moment';
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {GenericObject, Report} from '@ion/core/src/types/entities';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {CreateGeoFenceTypeEnum} from '@ion/dls/src/types/types';
import {ReportsMode} from '../../types/redux';
import {
  ErrorMessage,
  Grid,
  ReportSettings,
  ReportsFilter,
  ReportsHistory,
  Timeline
} from '../reducers/reports';
import {ReportsReducerState, TrackingReducerState} from '../types';

// input selectors ...
const selectIsInitialized       = ({reports: {isInitialized}}): boolean => isInitialized;
const selectHistory             = ({reports: {history}}): ReportsHistory => history;
const selectSettings            = ({reports: {settings}}): ReportSettings => settings;
const selectMode                = ({reports: {mode}}): ReportsMode | null => mode;
const selectedReport            = ({reports: {availableReports: {selected}}}) => selected;
const selectedReportURL         = ({reports: {availableReports: {selectedUrl}}}) => selectedUrl;
const selectGrid                = ({reports: {grid}}): Grid => grid;
const selectShippingGrid        = ({reports: {shippingGrid}}): Grid => shippingGrid;
const selectFilters             = ({reports: {filters}}): ReportsFilter => filters;
const selectGridFilterModel     = ({reports: {gridFilters}}): FilterModel => gridFilters.filterModel;
const selectTimeline            = ({reports: {timeline}}): Timeline => timeline;
const selectErrorMsg            = ({reports: {errorMsg}}): ErrorMessage => errorMsg;
const selectBoundaryPanelOpen   = ({reports: {boundaryPanelOpen}}): boolean => boundaryPanelOpen;
const selectIsTokenRegistered   = ({reports: {isTokenRegistered}}): boolean => isTokenRegistered;
const selectAssetFilterLoading  = ({reports: {history}}): boolean => history.assetsLoading;
const selectDateValue           = ({tracking: {filters: {date}}}: TrackingReducerState): string => date?.period;
const selectShipmentFilters     = ({reports: {shipmentHistory: {filters}}}): ReportsFilter => filters;
const selectHistoryProps        = ({reports: {mapProps}}): ReportsHistory => mapProps;
const selectIsMobileMapView     = ({
  reports: {history: {isMobileMapView}}
}: ReportsReducerState): boolean => isMobileMapView;
const selectBoundaryTool        = ({
  reports: {selectedBoundaryTool}
}: ReportsReducerState): CreateGeoFenceTypeEnum => selectedBoundaryTool;
const selectedDVRAssets         = ({reports: {dvrAssets}}): GenericObject => dvrAssets;

// output selectors ...
export const isReportsInitializedSelector = createSelector(selectIsInitialized,
  (isInitialized: boolean): boolean => isInitialized
);

export const historyPropsSelector = createDeepEqualSelector(selectHistoryProps,
  (props: GenericObject): GenericObject => props
);

export const reportsModeSelector = createDeepEqualSelector(selectMode,
  (mode: ReportsMode | null): ReportsMode | null => mode
);

export const selectedReportSelector = createDeepEqualSelector(selectedReport,
  (selectedUrl: Report): Report => selectedUrl
);

export const selectedReportURLSelector = createSelector(selectedReportURL,
  (selected: string): string => selected
);

export const reportsGridColumnStateSelector = createDeepEqualSelector(selectGrid,
  (grid: Grid): ColumnState[] => grid.columnState
);

export const shippingReportsGridColumnStateSelector = createDeepEqualSelector(selectShippingGrid,
  (shippingGrid: Grid): ColumnState[] => shippingGrid.columnState
);

export const isShippingReportsGridChange = createDeepEqualSelector(selectShippingGrid,
  (shippingGrid: Grid) => shippingGrid?.hasUpdate
);

export const reportsHistorySelector = createDeepEqualSelector(selectHistory,
  (history: ReportsHistory): any[] => history.history
);

export const showEventsBetweenTripsSelector = createDeepEqualSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showEventsBetweenTrips
);

export const showTripVisibleSelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showTripVisible
);

export const showSummarySelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showSummaryCards
);

export const showDistanceTravelledSelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showDistanceTravelled
);
export const showIdlingTimeSelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showIdlingTime
);
export const showDrivingTimeSelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showDrivingTime
);
export const showHarshEventsSelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showHarshEvents
);

export const showTotalAssetsSelector = createSelector(selectSettings,
  (settings: ReportSettings): boolean => settings.showTotalAssets
);

export const reportsHistoryByAssetSelector = createDeepEqualSelector(selectHistory,
  (history: ReportsHistory): GenericObject => history.historyByAsset
);

export const reportsHoveredEventByAssetSelector = createDeepEqualSelector(selectHistory,
  (history: ReportsHistory) => history.hoveredEvent
);

export const reportsHistoryLoadingSelector = createSelector(selectHistory,
  (history: ReportsHistory): boolean => history.loading
);

export const reportsHistoryFilterLoadingSelector = createSelector(selectHistory,
  (history: ReportsHistory): boolean => history?.filterLoading
);

export const assetFilterLoadingSelector = createSelector(selectAssetFilterLoading,
  (assetsLoading: boolean): boolean => assetsLoading
);

export const reportsHistoryStartDateSelector = createDeepEqualSelector(selectHistory,
  (history: ReportsHistory): Moment => history?.startDate
);

export const reportsHistoryEndDateSelector = createDeepEqualSelector(selectHistory,
  (history: ReportsHistory): Moment => history?.endDate
);

export const reportsHistoryAssetsSelector = createDeepEqualSelector(selectHistory,
  (history: ReportsHistory) => history?.assets
);

export const reportsFiltersSelector = createDeepEqualSelector(selectFilters,
  (filters: ReportsFilter): ReportsFilter => filters
);

export const gridFilterModelSelector = createDeepEqualSelector(selectGridFilterModel,
  (filterModel: FilterModel): FilterModel => filterModel
);

export const reportsTimelineSelector = createDeepEqualSelector(selectTimeline,
  (timeline: Timeline): Timeline => timeline
);

export const reportsErrorMsgSelector = createDeepEqualSelector(selectErrorMsg,
  (errorMsg: ErrorMessage): ErrorMessage => errorMsg
);

export const reportsErrorMsgShowSelector = createSelector(selectErrorMsg,
  (errorMsg: ErrorMessage): boolean => errorMsg.show
);

export const reportsErrorMsgCurrentRequestSelector = createDeepEqualSelector(selectErrorMsg,
  (errorMsg: ErrorMessage) => errorMsg.currentRequest
);

export const reportsBoundaryPanelOpenSelector = createSelector(selectBoundaryPanelOpen,
  (boundaryPanelOpen: boolean): boolean => boundaryPanelOpen
);

export const selectedBoundaryToolSelector = createSelector(selectBoundaryTool,
  (selectedBoundaryTool: CreateGeoFenceTypeEnum): CreateGeoFenceTypeEnum => selectedBoundaryTool
);

export const isTokenRegisteredSelector = createSelector(selectIsTokenRegistered,
  (isTokenRegistered: boolean): boolean => isTokenRegistered
);

export const dateValueSelector = createSelector(selectDateValue,
  (dateValue: string): string => dateValue
);

export const shipmentFiltersSelector = createSelector(selectShipmentFilters,
  (filters: ReportsFilter): ReportsFilter => filters
);

export const isMobileMapViewSelector = createSelector(selectIsMobileMapView,
  (isMobileMapView: boolean): boolean => isMobileMapView
);

export const selectedDVRAssetsSelector = createSelector(selectedDVRAssets,
  (dvrAssets: GenericObject): GenericObject => dvrAssets
);
